import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import client from '../api/client';

function Chat({ token }) {
    const { mentorId } = useParams();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [conversationId, setConversationId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [models, setModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState('openai/gpt-3.5-turbo');
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        const fetchModels = async () => {
            try {
                const response = await client.get('/chat/models');
                setModels(response.data.models);
            } catch (err) {
                console.error('Failed to fetch models', err);
            }
        };

        const fetchHistory = async () => {
            try {
                const res = await client.get('/conversations');
                const existing = res.data.conversations.find(c => c.mentor_id === parseInt(mentorId));
                if (existing) {
                    setConversationId(existing.id);
                    const msgRes = await client.get(`/conversations/${existing.id}/messages`);
                    setMessages(msgRes.data.messages);
                }
            } catch (err) {
                console.error(err);
            }
        };

        fetchModels();
        fetchHistory();
    }, [mentorId, token]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim()) return;

        const userMsg = { role: 'user', content: input };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);

        try {
            const response = await client.post('/chat/send', {
                mentor_id: mentorId,
                conversation_id: conversationId,
                message: userMsg.content,
                model: selectedModel
            });

            setConversationId(response.data.conversation_id);
            const asstMsg = { role: 'assistant', content: response.data.message };
            setMessages(prev => [...prev, asstMsg]);
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || 'Failed to send message');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="chat-container">
            <div className="chat-window card">
                <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label htmlFor="model-select" style={{ fontWeight: '600' }}>Model:</label>
                    <select
                        id="model-select"
                        value={selectedModel}
                        onChange={(e) => setSelectedModel(e.target.value)}
                        style={{
                            padding: '8px',
                            borderRadius: '6px',
                            backgroundColor: '#2c2c2c',
                            color: 'var(--text-color)',
                            border: '1px solid #444',
                            flex: 1
                        }}
                    >
                        {models.length > 0 ? (
                            models.map((model) => (
                                <option key={model.id} value={model.id}>
                                    {model.name}
                                </option>
                            ))
                        ) : (
                            <option value="openai/gpt-3.5-turbo">openai/gpt-3.5-turbo (default)</option>
                        )}
                    </select>
                </div>

                <div className="messages">
                    {messages.map((msg, idx) => (
                        <div key={idx} className={`message ${msg.role}`} style={{ whiteSpace: 'pre-wrap' }}>
                            {msg.content}
                        </div>
                    ))}
                    {loading && <div className="message assistant">Typing...</div>}
                    <div ref={messagesEndRef} />
                </div>
                <form onSubmit={handleSend} style={{ display: 'flex', gap: '10px' }}>
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Type your message..."
                        disabled={loading}
                    />
                    <button type="submit" disabled={loading}>Send</button>
                </form>
            </div>
        </div>
    );
}

export default Chat;
